import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Snackbar,
    Alert,
    Button,
    InputBase,
    Chip,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,

    Tab,
    Tabs,
    Skeleton,
} from '@mui/material';
import {
    QuestionAnswer as FaqIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore,

    Clear as ClearIcon,
    HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { LibraryFaq } from '../types/library';
import { libraryApi } from '../services/api/libraryApi';
import FaqDialog from '../components/library/FaqDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import PageHeader from '../components/common/PageHeader';

const FaqPage: React.FC = () => {
    const { user } = useAuthStore();
    const isAdmin = (user?.accountType ?? 0) >= AccountType.CarbonCareAdmin;

    const [loading, setLoading] = useState(true);
    const [faqs, setFaqs] = useState<LibraryFaq[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [expandedId, setExpandedId] = useState<number | false>(false);
    const [faqDialogOpen, setFaqDialogOpen] = useState(false);
    const [editingFaq, setEditingFaq] = useState<LibraryFaq | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean; title: string; message: string; onConfirm: () => void;
    }>({ open: false, title: '', message: '', onConfirm: () => {} });

    const openConfirm = (title: string, message: string, onConfirm: () => void) =>
        setConfirmDialog({ open: true, title, message, onConfirm });
    const closeConfirm = () => setConfirmDialog((prev) => ({ ...prev, open: false }));
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadFaqs = useCallback(async () => {
        try {
            const data = await libraryApi.getFaqs();
            setFaqs(data);
        } catch (err) {
            console.error('Failed to load FAQs:', err);
        }
    }, []);

    useEffect(() => {
        loadFaqs().finally(() => setLoading(false));
    }, [loadFaqs]);

    const showSnackbar = (message: string, severity?: string) => {
        if(severity){
            setError(message)
        }else{
            setSuccess(message)
        }
    };

    const handleFaqSubmit = async (data: any) => {
        try {
            if (editingFaq) {
                await libraryApi.updateFaq(editingFaq.id, data);
                showSnackbar('FAQ updated successfully');
            } else {
                await libraryApi.createFaq(data);
                showSnackbar('FAQ created successfully');
            }
            await loadFaqs();
        } catch {
            showSnackbar('Failed to save FAQ', 'error');
        }
    };

    const handleDeleteFaq = (faq: LibraryFaq) => {
        openConfirm(
            'Delete FAQ',
            'Are you sure you want to delete this FAQ? This action cannot be undone.',
            async () => {
                try {
                    await libraryApi.deleteFaq(faq.id);
                    showSnackbar('FAQ deleted');
                    if (selectedCategory !== 'All') {
                        const remainingInCategory = faqs.filter(
                            (f) => f.id !== faq.id && normalizeCategory(f.category) === selectedCategory,
                        );
                        if (remainingInCategory.length === 0) setSelectedCategory('All');
                    }
                    await loadFaqs();
                } catch {
                    showSnackbar('Failed to delete FAQ', 'error');
                }
            },
        );
    };

    const normalizeCategory = (cat: string | null | undefined): string => {
        // Preserve the casing as stored in the DB so acronyms like ISO, API, CO₂
        // render correctly. Only trim whitespace and fall back to 'General'.
        const s = (cat || '').trim();
        return s || 'General';
    };

    // Derived state
    const categories = useMemo(() => {
        const cats = Array.from(new Set(faqs.map((f) => normalizeCategory(f.category)))).sort((a, b) => a.localeCompare(b));
        return ['All', ...cats];
    }, [faqs]);

    const filteredFaqs = useMemo(() => {
        return faqs.filter((faq) => {
            const cat = normalizeCategory(faq.category);
            const matchCat = selectedCategory === 'All' || cat === selectedCategory;
            if (!matchCat) return false;
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            return faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q);
        });
    }, [faqs, searchQuery, selectedCategory]);

    const grouped = useMemo(() => {
        return filteredFaqs.reduce((acc, faq) => {
            const cat = normalizeCategory(faq.category);
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(faq);
            return acc;
        }, {} as Record<string, LibraryFaq[]>);
    }, [filteredFaqs]);

    const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    const handleAccordionChange = (id: number) => (_: any, isExpanded: boolean) => {
        setExpandedId(isExpanded ? id : false);
    };

    // Extracted to keep the FAQ list JSX from nesting functions too deeply (S2004)
    const renderFaqAdminActions = (faq: LibraryFaq) => (
        <Box
            sx={{ display: 'flex', gap: 0.25, ml: 1 }}
            onClick={(e) => e.stopPropagation()}
        >
            <Tooltip title="Edit FAQ">
                <IconButton
                    size="small"
                    onClick={() => {
                        setEditingFaq(faq);
                        setFaqDialogOpen(true);
                    }}
                    sx={{ color: '#6b7280', '&:hover': { color: '#17a2b8', bgcolor: 'rgba(23,162,184,0.08)' } }}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete FAQ">
                <IconButton
                    size="small"
                    onClick={() => handleDeleteFaq(faq)}
                    sx={{ color: '#6b7280', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    return (
        <Box>
            <PageHeader
                description="Answers to common questions about the platform."
                actions={
                    isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setEditingFaq(null);
                                setFaqDialogOpen(true);
                            }}
                            sx={{
                                background: 'linear-gradient(135deg, #17a2b8 0%, #117a8b 100%)',
                                boxShadow: '0 4px 12px rgba(23,162,184,0.30)',
                                '&:hover': { background: 'linear-gradient(135deg, #138496 0%, #0e6677 100%)' },
                            }}
                        >
                            Add FAQ
                        </Button>
                    )
                }
            />

            {/* Search + Filter Bar */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                {/* Search */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 220,
                        bgcolor: '#f8fafc',
                        border: '1.5px solid rgba(0,0,0,0.10)',
                        borderRadius: 2,
                        px: 1.5,
                        height: 40,
                        '&:focus-within': { borderColor: '#17a2b8', boxShadow: '0 0 0 3px rgba(23,162,184,0.12)', bgcolor: '#fff' },
                        transition: 'all 0.2s',
                    }}
                >
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
                    <InputBase
                        placeholder="Search questions or answers…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ fontSize: '0.875rem', flex: 1 }}
                    />
                    {searchQuery && (
                        <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ ml: 0.5, p: 0.5 }}>
                            <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>

                {/* Stats */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loading ? (
                        <Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: '12px' }} />
                    ) : (
                        <Chip
                            icon={<HelpIcon sx={{ fontSize: '14px !important' }} />}
                            label={`${filteredFaqs.length} of ${faqs.length} FAQs`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderColor: '#e5e7eb' }}
                        />
                    )}
                </Box>
            </Paper>

            {/* Category Tabs */}
            {loading ? (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    {Array.from({ length: 4 }, (_, i) => `faq-tab-skeleton-${i}`).map((tabKey, idx) => (
                        <Skeleton key={tabKey} variant="rounded" width={90 + idx * 14} height={28} sx={{ borderRadius: '6px' }} />
                    ))}
                </Box>
            ) : (
            <Box sx={{ mb: 3 }}>
                <Tabs
                    value={selectedCategory}
                    onChange={(_, v) => setSelectedCategory(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTabs-indicator': { backgroundColor: '#17a2b8', height: 2.5 },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            minHeight: 40,
                            color: '#6b7280',
                            '&.Mui-selected': { color: '#17a2b8', fontWeight: 700 },
                        },
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                    }}
                >
                    {categories.map((cat) => {
                        const count = cat === 'All' ? faqs.length : faqs.filter((f) => normalizeCategory(f.category) === cat).length;
                        return (
                            <Tab
                                key={cat}
                                value={cat}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {cat}
                                        <Chip
                                            label={count}
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                bgcolor: selectedCategory === cat ? 'rgba(23,162,184,0.12)' : '#f3f4f6',
                                                color: selectedCategory === cat ? '#17a2b8' : '#9ca3af',
                                                '& .MuiChip-label': { px: 0.75 },
                                            }}
                                        />
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>
            </Box>
            )}

            {/* FAQ Content */}
            {(() => {
                if (loading) return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Array.from({ length: 6 }, (_, i) => `faq-item-skeleton-${i}`).map((itemKey) => (
                        <Box
                            key={itemKey}
                            sx={{
                                border: '1.5px solid rgba(0,0,0,0.08)',
                                borderRadius: '10px',
                                background: '#fff',
                                px: 2.5,
                                py: 1.5,
                                minHeight: 56,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Skeleton variant="rounded" width={26} height={26} sx={{ borderRadius: '6px', flexShrink: 0 }} />
                            <Skeleton variant="text" sx={{ fontSize: '0.9375rem', flex: 1 }} />
                            <Skeleton variant="circular" width={24} height={24} />
                        </Box>
                    ))}
                </Box>
                );
                if (filteredFaqs.length === 0) return (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 8,
                        px: 4,
                        border: '1.5px dashed rgba(0,0,0,0.10)',
                        borderRadius: 3,
                        background: '#fafbfc',
                    }}
                >
                    <FaqIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600} color="#9ca3af">
                        No FAQs found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {searchQuery ? 'Try a different search term or clear filters.' : 'No FAQs have been added yet.'}
                    </Typography>
                </Box>
                );
                return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {groupKeys.map((category) => (
                        <Box key={category}>
                            {groupKeys.length > 1 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 4,
                                            height: 20,
                                            borderRadius: 2,
                                            background: 'linear-gradient(180deg, #17a2b8 0%, #117a8b 100%)',
                                        }}
                                    />
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#374151', letterSpacing: '0.01em' }}>
                                        {category}
                                    </Typography>
                                    <Chip
                                        label={grouped[category].length}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            bgcolor: 'rgba(23,162,184,0.10)',
                                            color: '#17a2b8',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {grouped[category].map((faq, idx) => (
                                    <Accordion
                                        key={faq.id}
                                        expanded={expandedId === faq.id}
                                        onChange={handleAccordionChange(faq.id)}
                                        slotProps={{ transition: { unmountOnExit: true } }}
                                        elevation={0}
                                        disableGutters
                                        sx={{
                                            border: '1.5px solid',
                                            borderColor: expandedId === faq.id ? 'rgba(23,162,184,0.30)' : 'rgba(0,0,0,0.08)',
                                            borderRadius: '10px !important',
                                            background: expandedId === faq.id ? 'rgba(23,162,184,0.02)' : '#fff',
                                            boxShadow: expandedId === faq.id ? '0 4px 14px rgba(23,162,184,0.10)' : 'none',
                                            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                                            '&:before': { display: 'none' },
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={
                                                <ExpandMore
                                                    sx={{
                                                        color: expandedId === faq.id ? '#17a2b8' : '#9ca3af',
                                                        transition: 'color 0.2s',
                                                    }}
                                                />
                                            }
                                            sx={{
                                                px: 2.5,
                                                py: 1.5,
                                                minHeight: 56,
                                                '& .MuiAccordionSummary-content': { my: 0, alignItems: 'center' },
                                            }}
                                        >
                                            {/* Question number */}
                                            <Box
                                                sx={{
                                                    width: 26,
                                                    height: 26,
                                                    borderRadius: 1.5,
                                                    background: expandedId === faq.id
                                                        ? 'linear-gradient(135deg, #17a2b8, #117a8b)'
                                                        : 'rgba(0,0,0,0.06)',
                                                    color: expandedId === faq.id ? '#fff' : '#9ca3af',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    mr: 2,
                                                    flexShrink: 0,
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                {String(idx + 1).padStart(2, '0')}
                                            </Box>

                                            <Typography
                                                variant="body1"
                                                fontWeight={expandedId === faq.id ? 700 : 500}
                                                sx={{
                                                    color: expandedId === faq.id ? '#17a2b8' : '#374151',
                                                    flexGrow: 1,
                                                    fontSize: '0.9375rem',
                                                    lineHeight: 1.5,
                                                    transition: 'color 0.2s',
                                                }}
                                            >
                                                {faq.question}
                                            </Typography>

                                            {/* Admin actions */}
                                            {isAdmin && renderFaqAdminActions(faq)}
                                        </AccordionSummary>

                                        <AccordionDetails
                                            sx={{
                                                px: 2.5,
                                                pt: 0,
                                                pb: 2.5,
                                                ml: '42px', // align with question text
                                                borderTop: '1px solid rgba(23,162,184,0.10)',
                                                mt: -0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#4b5563',
                                                    lineHeight: 1.8,
                                                    whiteSpace: 'pre-wrap',
                                                    pt: 1.5,
                                                }}
                                            >
                                                {faq.answer}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
                );
            })()}

            {/* Dialogs */}
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={() => { closeConfirm(); confirmDialog.onConfirm(); }}
                onClose={closeConfirm}
            />

            <FaqDialog
                open={faqDialogOpen}
                onClose={() => setFaqDialogOpen(false)}
                onSubmit={handleFaqSubmit}
                faq={editingFaq}
            />

            {/* Success/Error Snackbars */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                {success}
                </Alert>
            </Snackbar>
            
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                {error}
                </Alert>
            </Snackbar>
                </Box>
    );
};

export default FaqPage;
